/**
 * Ehler Fisher Transform Calculator
 *
 * The Fisher Transform converts prices into a Gaussian normal distribution,
 * making it easier to identify turning points and price extremes.
 * Developed by John Ehlers.
 *
 * Formula:
 * 1. midpoint = (High + Low) / 2
 * 2. LLV = Lowest(midpoint, Period)
 * 3. HHV = Highest(midpoint, Period)
 * 4. rawValue = 2 × ((midpoint - LLV) / (HHV - LLV) - 0.5)
 * 5. n = 0.33 × rawValue + 0.67 × n[previous]
 * 6. n = clamp(n, -0.9999, 0.9999)  // Prevent ln(0)
 * 7. EF = 0.5 × ln((1 + n) / (1 - n)) + 0.5 × EF[previous]
 * 8. EF Trigger = EF[previous]
 *
 * Range: Oscillates around 0, typically between ±3
 */
export default function () {
  function calculator(data) {
    const { period } = options;

    if (!data || data.length < period) {
      return data.map(() => ({ ef: undefined, trigger: undefined }));
    }

    // Helper function to get LLV and HHV of midpoint over period
    function getLLVHHV(endIndex) {
      let llv = Number.MAX_VALUE;
      let hhv = -Number.MAX_VALUE;

      for (let j = endIndex - period + 1; j <= endIndex; j++) {
        if (j < 0) continue;
        const d = data[j];
        if (d.high == null || d.low == null) continue;

        const midpoint = (d.high + d.low) / 2;
        llv = Math.min(llv, midpoint);
        hhv = Math.max(hhv, midpoint);
      }

      return { llv, hhv };
    }

    const results = [];
    let n = 0; // Smoothed normalized value
    let previousEF = 0;

    for (let i = 0; i < data.length; i++) {
      const d = data[i];

      // Need at least period bars
      if (i < period - 1 || d.high == null || d.low == null) {
        results.push({ ef: i > 0 ? previousEF : 0, trigger: 0 });
        continue;
      }

      // Get highest/lowest midpoint over period
      const { llv, hhv } = getLLVHHV(i);
      const midpoint = (d.high + d.low) / 2;

      // Calculate raw normalized value
      const range = Math.max(0.000001, hhv - llv); // Avoid division by zero
      const rawValue = 2 * ((midpoint - llv) / range - 0.5);

      // Apply exponential smoothing (EMA-like)
      n = 0.33 * rawValue + 0.67 * n;

      // Clamp to prevent ln(0) or ln(negative) in Fisher Transform
      if (n > 0) {
        n = Math.min(n, 0.9999);
      } else if (n < 0) {
        n = Math.max(n, -0.9999);
      }

      // Calculate Fisher Transform
      // FT = 0.5 × ln((1 + n) / (1 - n))
      const ft = 0.5 * Math.log((1 + n) / (1 - n));

      // Apply smoothing to Fisher Transform
      const ef = 0.5 * ft + 0.5 * previousEF;

      // Trigger line is previous EF value
      const trigger = previousEF;

      results.push({ ef, trigger });

      previousEF = ef;
    }

    return results;
  }

  let options = {
    period: 10,
  };

  calculator.undefinedLength = function () {
    return options.period - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...options, ...x };
    return calculator;
  };

  return calculator;
}
